import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router";
import constants from '../../../constants/constants';
import { Button, Card, Checkbox, Col, Divider, Form, message, Typography, Popconfirm, Row, Select, Space, Spin, Tag } from 'antd';
import { DOB } from '../../../globalFunctions/GlobalFunctions';
import { fetchAdminApi } from '../../../services/api';
import { COOKIE, getCookie } from '../../../services/cookie';
import moment from 'moment';

const { Text } = Typography;
const { Option } = Select;

const AdmissionAssessForm = () => {
	const history = useHistory();
	const [form] = Form.useForm();
	const adminClientValue = JSON.parse(sessionStorage.getItem("adminClientValue"));
	let enquiryDetailsfromSession = JSON.parse(sessionStorage.getItem("enquiryDetails"));
	const [loader, setLoader] = useState(false);
	const [noteId, setNoteId] = useState(null);
	const [isSigned, setIsSigned] = useState(false);
	const [noteData, setNoteData] = useState(false);
	const [assessmentQuestions, setAssessmentQuestions] = useState(null);
	const isAdmin = getCookie(COOKIE.LoginType) === "0" ? false : true;
	const [screeningData, setScreeningData] = useState(null);

	const current = new Date();
	const [date, setDate] = useState({
		date: current.getDate(),
		month: (current.getMonth() + 1).toString().padStart(2, '0'),
		year: current.getFullYear()
	});
	const handleDayChange = (value) => {
		setDate({
			...date,
			date: value
		});
	};
	const handleMonthChange = (value) => {
		setDate({
			...date,
			month: value
		});
	};
	const handleYearChange = (value) => {
		setDate({
			...date,
			year: value
		});
	};

	useEffect(() => {
		getRecoveryPlanAnswer();
	}, []);

	const getRecoveryPlanAnswer = () => {
		let enquiryId = adminClientValue?.enquiry_info?.enquiry_id || adminClientValue?.enquiryId || enquiryDetailsfromSession?.enquiry_id;
		setLoader(true);

		fetchAdminApi(`/get-admission-assessment/${enquiryId}`, 'get')
			.then((res) => {
				if (res?.code && res.code === 200) {
					// overall save
					setNoteData(res?.data);

					let data = null;
					if (res?.data?.admission_assessment_data) {
						data = res?.data?.admission_assessment_data?.answers;
					} else {
						data = res?.data?.screening_form;
					}

					if (data?.assessment_questions) {
						setAssessmentQuestions(data.assessment_questions || null);
					}

					// diagnosis
					const diagnosisMappedValues = mapDiagnosisValues(res?.data?.diagnosis);
					const formValues = { ...data, ...diagnosisMappedValues };
					form.setFieldsValue(formValues);

					setScreeningData(res?.data?.self_assessment_data);

					// note data
					if (res?.data?.admission_assessment_data?.notes_id) {
						setNoteId(res?.data?.admission_assessment_data?.notes_id);
					} else {
						setNoteId(null);
					}
					if (res?.data?.admission_assessment_data?.signed_by) {
						setIsSigned(true);
					}
					if (res?.data?.admission_assessment_data?.dt_created) {
						const noteDateObj = new Date(res?.data?.admission_assessment_data?.dt_created);

						const formattedDate = {
							date: noteDateObj.getDate().toString().padStart(2, '0'),
							month: (noteDateObj.getMonth() + 1).toString().padStart(2, '0'),
							year: noteDateObj.getFullYear().toString()
						};
						setDate(formattedDate);
					}
				} else {
					setNoteData(null);
					setNoteId(null);
					setIsSigned(false);

					message.error(res?.message);
				}
			})
			.catch((err) => {
				setNoteData(null);
				setNoteId(null);
				setIsSigned(false);

				message.error("Failed to load recovery plan data.");
			})
			.finally(() => {
				setLoader(false);
			});
	};

	const problemQuestions = constants.ComprehensivePsychosocialOptions.map(section => ({
		...section,
		questions: section.questions.filter(q => q.name.includes('_Problem'))
	})).filter(section => section.questions.length > 0);

	const tagRender = (props) => {
		const { label, closable, onClose } = props;

		return (
			<Tag
				closable={!isSigned && closable}
				onClose={onClose}
				style={{
					whiteSpace: 'normal',
					overflowWrap: 'break-word',
					wordBreak: 'break-word',
					maxWidth: '100%',
					lineHeight: '1.2',
				}}
				className={`!h-auto !inline-flex !items-center !break-words !whitespace-normal !text-sm ${isSigned ? 'ant-select-disabled' : ''}`}
			>
				{label}
			</Tag>
		);
	};

	const sortByLabelSafe = (options) =>
		Array.isArray(options)
			? [...options].sort((a, b) => (a.label || '').localeCompare(b.label || ''))
			: [];

	const mapDiagnosisValues = (diagnosisObj = {}) => {
		const isLegacyEntry = (key, val) =>
			Array.isArray(val) && val.length === 3 && typeof val[0] === 'string' && typeof val[2] === 'string';

		const filtered = {};
		Object.entries(diagnosisObj).forEach(([key, values]) => {
			if (!isNaN(Number(key))) return;
			if (isLegacyEntry(key, values)) return;

			if (Array.isArray(values) && values.length > 0) {
				// Normalize all IDs to strings to match Select option values
				filtered[key] = values.map(v => String(v));
			}
		});
		return filtered;
	};

	const diagnosisFields = [
		{
			label: 'Primary Addiction Diagnosis',
			name: 'primary_diagnosis',
			options: sortByLabelSafe(constants.primarySecondaryTertiaryOptions)
		},
		{
			label: 'Secondary Addiction Diagnosis',
			name: 'secondary_diagnosis',
			options: sortByLabelSafe(constants.primarySecondaryTertiaryOptions)
		},
		{
			label: 'Tertiary Addiction Diagnosis',
			name: 'tertiary_diagnosis',
			options: sortByLabelSafe(constants.primarySecondaryTertiaryOptions)
		},
		{
			label: 'Primary Mental Diagnosis',
			name: 'other_cd_diagnosis',
			options: sortByLabelSafe(constants.mentalHealthDiagOptions)
		},
		{
			label: 'Secondary Mental Diagnosis',
			name: 'mental_health_diagnosis_2',
			options: sortByLabelSafe(constants.mentalHealthDiagOptions)
		},
		{
			label: 'Tertiary Mental Diagnosis',
			name: 'mental_health_diagnosis_3',
			options: sortByLabelSafe(constants.mentalHealthDiagOptions)
		},
		{
			label: 'Primary Other Diagnosis',
			name: 'other_diagnosis_1',
			options: sortByLabelSafe(constants.Otheroptions)
		},
		{
			label: 'Secondary Other Diagnosis',
			name: 'other_diagnosis_2',
			options: sortByLabelSafe(constants.Otheroptions)
		},
		{
			label: 'Tertiary Other Diagnosis',
			name: 'other_diagnosis_3',
			options: sortByLabelSafe(constants.Otheroptions)
		},
	];
	const panelTitle = (title) => {
		return <div className='mt-5 mb-4 text-base italic underline font-bold'>{title}</div>
	};
	const DiagnosisSelect = ({ label, name, options }) => (
		<Col xs={24} lg={12} xl={8}>
			<Form.Item label={label} name={name}>
				<Select
					mode='tags'
					placeholder={`Select ${label}`}
					disabled
					allowClear
					showSearch
					optionFilterProp="children"
					optionLabelProp="label"
					dropdownStyle={{ whiteSpace: 'normal', overflowWrap: 'break-word' }}
					tagRender={tagRender}
					filterOption={(input, option) =>
						(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
					}
				>
					{options?.map(option => (
						<Select.Option key={option.value} value={option.value} label={option.label}>
							<span className="whitespace-normal">{option.label}</span>
						</Select.Option>
					))}
				</Select>
			</Form.Item>
		</Col>
	);

	const handleEsignConfirm = () => {
		form
			.validateFields()
			.then((values) => {
				onFinishForm(values, "esign");
			})
			.catch(() => {
				message.error("Please try again later");
			});
	};

	const handleBackClick = () => {
		if (history.length > 1) {
			history.goBack();
		} else {
			handleRedirect();
		}
	};

	const handleRedirect = () => {
		if (isAdmin) {
			if (enquiryDetailsfromSession?.enquiry_id) {
				// Enquiry flow: remove admin client session
				sessionStorage.removeItem("adminClientValue");
				history.push("/view-enquiry");
			} else if (adminClientValue) {
				// Client flow: keep admin client session
				history.push("/view-client");
			}
		} else {
			history.push("/dashboard");
		}
	};

	const escapeHtml = (value) => {
		if (value === null || value === undefined) return "";

		return String(value)
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#039;");
	};

	const createPsychosocialNotesContent = (values) => {
		const sections = [];

		problemQuestions.forEach((section) => {
			const questions = [];

			section.questions.forEach((question) => {
				const selectedValues = values?.[question.name];

				if (
					selectedValues &&
					Array.isArray(selectedValues) &&
					selectedValues.length > 0
				) {
					questions.push(
						`<p><b>${escapeHtml(question.label)}:</b><br />${selectedValues.map((value) => `• ${escapeHtml(value)}`).join("<br />")}</p>`
					);
				}
			});

			if (questions.length > 0) {
				sections.push(`<p><b><u>${escapeHtml(section.topic)}</u></b></p>${questions.join("")}`);
			}
		});

		if (sections.length === 0) {
			return "";
		}

		return `<p><b><u><i>Comprehensive Psychosocial Assessment:</i></u></b></p><br/>${sections.join("<br />")}`;
	};

	const createNotesContent = (values) => {
		const paragraphs = [];

		// --------------------------------------------------
		// Comprehensive Psychosocial Assessment
		// --------------------------------------------------
		const psychosocialContent = createPsychosocialNotesContent(values);

		if (psychosocialContent) {
			paragraphs.push(`<div>${psychosocialContent}</div>`);
		}

		// --------------------------------------------------
		// LOCADTR Assessment
		// --------------------------------------------------
		const indicatedLevelOfCare = assessmentQuestions?.find(
			question => question.care
		)?.care;

		if (indicatedLevelOfCare) {
			paragraphs.push(`<div><p><b><i><u>${escapeHtml(constants.LocatrAssessment)}:</u></i></b></p><p><b>Indicated level of care:</b>${escapeHtml(indicatedLevelOfCare)}</p></div>`);
		}
		// --------------------------------------------------
		// Global
		// --------------------------------------------------
		const gafValue = values?.gaf_scale;
		if (gafValue) {
			const gafOption = constants.GAFScaleOptions.find(
				option => option.value === gafValue
			);

			if (gafOption) {
				paragraphs.push(`<p><b><i><u>Global Assessment of Functioning (GAF) Scale: </u></i></b></p><p><b>${escapeHtml(gafOption.score)}:</b> ${escapeHtml(gafOption.description)}</p>`);
			}
		}

		// --------------------------------------------------
		// Diagnosis
		// --------------------------------------------------
		if (Array.isArray(diagnosisFields)) {
			const diagnosisValues = [];

			diagnosisFields.forEach((field) => {
				const value = values?.[field.name];

				if (
					value !== undefined &&
					value !== null &&
					(Array.isArray(value) ? value.length > 0 : value !== "")
				) {
					const displayValues = Array.isArray(value)
						? value.map((val) => {
							const option = field.options?.find(
								(option) =>
									option.value === val ||
									option.key === val
							);

							return option?.label || val;
						})
						: (() => {
							const option = field.options?.find(
								(option) =>
									option.value === value ||
									option.key === value
							);

							return [option?.label || value];
						})();

					diagnosisValues.push(
						`<p><b>${escapeHtml(field.label)}:</b> ${escapeHtml(displayValues.join(", "))}</p>`
					);
				}
			});

			paragraphs.push(`<div><p><b><i><u>Initial Diagnosis:</u></i></b></p> ${diagnosisValues.join("")}</div>`);
		}

		// --------------------------------------------------
		// Summary
		// --------------------------------------------------
		const screeningAssessment = values?.screening_assessment;
		if (screeningAssessment) {
			const summaryText = getScreeningRadioOptions().find(
				option => option.value === screeningAssessment
			)?.label;

			if (summaryText) {
				paragraphs.push(`<div><p><b><i><u>Summary of Feedback given to the Recipient Based on the Results of the Screening: </u></i></b><br />${escapeHtml(summaryText)}</p></div>`);
			}
		}

		// --------------------------------------------------
		// Recommendations
		// --------------------------------------------------
		const selectedRecommendations = values?.recommendation || [];

		if (selectedRecommendations.length) {
			const recommendationLabels = constants.RecommendationOptions
				.filter(option =>
					selectedRecommendations.includes(option.value)
				)
				.map(option => escapeHtml(option.label));

			if (recommendationLabels.length) {
				paragraphs.push(`<div><p><b><i><u>Recommendation: </u></i></b><br />${recommendationLabels.map(label => `${label}`).join(", ")}</p></div>`);
			}
		}

		// --------------------------------------------------
		// Admission Criteria
		// --------------------------------------------------
		if (
			values?.admission_criteria &&
			Array.isArray(values.admission_criteria) &&
			values.admission_criteria.length
		) {
			const criteria = values.admission_criteria
				.map((value) => {
					const option = constants.AdmissionCriteriaOptions.find(
						(item) => item.value === value
					);

					return option?.label || value;
				})
				.map((value) => escapeHtml(value))
				.join(" ");

			paragraphs.push(
				`<div><p><b><i><u>Admission Criteria:</u></i></b></p>` + `<p>${escapeHtml(constants.AdmissionAssessPara)} ${criteria}</p></div>`
			);
		}

		return `<b><u>DATA:</u></b>${paragraphs.join("<br />")}`;
	};

	const calculateCageTestCount = (selectedAttempts) => {
		// If no selectedAttempts or empty object, return 0
		if (!selectedAttempts || Object.keys(selectedAttempts).length === 0) {
			return 0;
		}

		// If B (Not Applicable) is selected, return '-'
		if (selectedAttempts.B?.checked) {
			return '-';
		}

		// Count selected CAGE items (C, A, G, E)
		const cageKeys = ['C', 'A', 'G', 'E'];
		let count = 0;

		cageKeys.forEach(key => {
			if (selectedAttempts[key]?.checked) {
				count++;
			}
		});

		return count;
	};
	const cage_test_count = calculateCageTestCount(screeningData?.selectedattempts);

	const getScreeningRadioOptions = () => {
		const dastScore = screeningData?.dast_test_count?.count || '-';
		const gamblingScore = screeningData?.gambling_count?.count || '-';
		const mmmmScore = screeningData?.mmmh_test_count?.count || '-';

		const positiveText = `Recipient was informed that based on the scores on the CAGE-AID: ${cage_test_count}, DAST: ${dastScore}, GAM: ${gamblingScore} and MMMH: ${mmmmScore} screening instruments, the recipient meets criteria for Coaching Counseling.`;

		const negativeText = `Recipient was informed that based on the scores on the CAGE-AID: ${cage_test_count}, DAST: ${dastScore}, GAM: ${gamblingScore} and MMMH: ${mmmmScore} screening instruments, the recipient does not meet criteria for Coaching Counseling.`;

		return [
			{
				label: positiveText,
				value: 'positive'
			},
			{
				label: negativeText,
				value: 'negative'
			}
		];
	};

	const onFinishForm = (values, type = null) => {
		setLoader(true);

		const currentDate = new Date();
		const admin_id = getCookie(COOKIE.ClientId);

		const formdata = { ...values };

		if (assessmentQuestions?.length > 0) {
			formdata.assessment_questions = assessmentQuestions;
		}

		// Create HTML content for notes_content
		const notesContent = createNotesContent(values);

		const payload = {
			notes_content: notesContent,
			notes_assesment: null,
			notes_plan: null,
			section_id: null,
			notes_type: "10",
			dt_created: `${date.year}-${date.month.padStart(2, "0")}-${date.date
				.toString()
				.padStart(2, "0")}`,
			answers: formdata,
			admin_id: admin_id
		};

		// Set client / enquiry ID
		if (adminClientValue?.clientId) {
			payload.client_id = adminClientValue.clientId;
		} else {
			if (enquiryDetailsfromSession?.client_id) {
				payload.client_id = enquiryDetailsfromSession.client_id;
			} else {
				payload.enquiry_id = enquiryDetailsfromSession?.enquiry_id;
			}
		}

		// E-sign
		if (type === "esign") {
			payload.signed_by = admin_id;
			payload.dt_updated = currentDate.toISOString().split("T")[0];
		}

		let url;
		let method;

		if (noteId) {
			url = "/edit-notes";
			method = "put";
			payload.notes_id = noteId;
		} else {
			url = "/add-notes";
			method = "post";
		}

		fetchAdminApi(url, method, payload)
			.then((res) => {
				if (res?.code === 200) {
					// Update date after successful save
					setDate({
						...date,
						date: currentDate.getDate().toString(),
						month: (currentDate.getMonth() + 1).toString(),
						year: currentDate.getFullYear().toString()
					});

					message.success(res?.message);
					handleRedirect();
				} else {
					message.error(res?.message || "Something went wrong");
				}
			})
			.catch((error) => {
				console.error("Error creating/updating note:", error);
				message.error("Failed to save note. Please try again.");
			})
			.finally(() => {
				setLoader(false);
			});
	};


	const headerForm = (type) => {
		let label = constants.AdmissionAssessForm;
		let clientName = "";
		let serialNo = "";

		const clientId = adminClientValue?.clientId;

		if (clientId) {
			// Client details
			clientName = adminClientValue?.client_name || "";
			serialNo = adminClientValue?.client_serial_number || "";
		} else {
			// Enquiry details
			clientName = (enquiryDetailsfromSession?.client_firstname + " " + enquiryDetailsfromSession?.client_lastname) || "";
			serialNo = enquiryDetailsfromSession?.enquiry_id || "";
		}

		if (type === "title") {
			return (
				<span>
					{label} - {clientName} (#{serialNo})
				</span>
			);
		}
	};

	return (
		<div className='add-note'>
			<div className='text-center bg-amber-300 text-lg font-semibold p-3'>
				{constants.Subtitle}
				<span className="py-0.5 px-3 ml-3 text-sm bg-white rounded-xl">
					{constants.AdmissionForm}
				</span>
			</div>
			<div className='bg-amber-100 text-base font-semibold p-2 mb-3'>
				<div className='container mx-auto'>
					<Space size={'small'} wrap direction="vertical">
						{headerForm('title')}
						<Button size='small' className='text-xs font-semibold bg-sky-800 text-white uppercase' onClick={handleBackClick}>{constants.BackBtn}</Button>
					</Space>
				</div></div>
			<div className='container mx-auto'>
				<div className="px-2 pb-2">
					{DOB(date.month, handleMonthChange, date.date, handleDayChange, date.year, handleYearChange, "Date", isSigned)}
					<Spin spinning={loader} size="large">
						<Card
						>
							<Form layout='vertical' className='mb-10 text-black client-details disabled-dark' onFinish={onFinishForm} form={form}>

								{panelTitle("Comprehensive Psychosocial Assessment:")}

								<div className="w-full pt-1">
									{problemQuestions.map((section, i) => (
										<div key={section.topic} className='my-2'>
											<h2 className='text-base font-bold underline'>{section.topic}</h2>
											{section.questions.map((question, index) => {
												return (
													<div key={question.name}>
														<Col xs={24}>
															<Form.Item label={question.label} name={question.name}>
																<Select
																	placeholder={question.placeholder}
																	mode='tags'

																	tagRender={tagRender}
																	dropdownStyle={{ whiteSpace: "normal" }}
																>
																	{question.options.map((option) => (
																		<Option key={option.key} value={option.label}>
																			<div className='whitespace-normal'>
																				{option.label}
																			</div>
																		</Option>
																	))}
																</Select>
															</Form.Item>
														</Col>
													</div>
												)
											})}
										</div>
									))}
								</div>

								<Divider />
								{panelTitle(`${constants.LocatrAssessment}:`)}
								<Col xs={24} lg={24}>
									{assessmentQuestions?.find(q => q.care)?.care && (
										<div className="mt-4 px-4 py-3 bg-[#e6f7ff] rounded border border-[#91d5ff]">
											<Text type="secondary">
												<span className="font-bold">Indicated level of care:</span>{" "}
												{assessmentQuestions.find(q => q.care).care}
											</Text>
										</div>
									)}
								</Col>

								<Col xs={24} className="pb-5 pt-6">
									<Form.Item
										name="gaf_scale"
										label={
											<span className="text-base font-bold italic underline mb-3">
												Global Assessment of Functioning (GAF) Scale
											</span>
										}
										className='mb-0'
									>
										<Select
											placeholder="Select GAF Score"
											showSearch
											disabled
											optionLabelProp="label"
											labelRender={({ label }) => (
												<div className="whitespace-normal">
													{label}
												</div>
											)}
											filterOption={(input, option) =>
												(option?.label ?? "")
													.toLowerCase()
													.includes(input.toLowerCase())
											}
											className='h-fit'
										>
											{constants.GAFScaleOptions.map(
												({ value, score, description }) => (
													<Select.Option
														key={value}
														value={value}
														label={`${score}: ${description}`}
													>
														<div className="whitespace-normal">
															<strong>{score}:</strong> {description}
														</div>
													</Select.Option>
												)
											)}
										</Select>
									</Form.Item>
								</Col>

								{panelTitle("Initial Diagnosis:")}
								<Row gutter={[12, 12]} >

									{diagnosisFields.map(field => (
										<DiagnosisSelect
											key={field.name}
											label={field.label}
											name={field.name}
											options={field.options}
										/>
									))}
								</Row>

								<Col xs={24} className='pt-3'>
									<Form.Item
										name="screening_assessment"
										label={
											<span className="text-base font-bold italic underline ">
												Summary of Feedback given to the Recipient Based on the Results of the Screening
											</span>
										}
										className='mb-0'
									>
										<div className='text-sm'>
											{
												getScreeningRadioOptions().find(
													option => option.value === noteData?.screening_form?.screening_assessment
												)?.label
											}
										</div>
									</Form.Item>
								</Col>

								<Col xs={24} lg={24} className='pt-5'>
									<Form.Item label={
										<span className="text-base font-bold italic underline ">
											Recommendation</span>
									}
										name="recommendation" allowClear>
										<Checkbox.Group className='w-full' disabled>
											<Row>
												{constants.RecommendationOptions
													.filter(option =>
														form.getFieldValue("recommendation")?.includes(option.value)
													)
													.map(option => (
														<Col
															xs={24}
															md={12}
															lg={8}
															xl={6}
															key={option.value}
														>
															<Checkbox value={option?.value}>{option.label}</Checkbox>
														</Col>
													))}
											</Row>
										</Checkbox.Group>
									</Form.Item>
								</Col>

								<Divider />

								{panelTitle("Admissions Decisions:")}
								<p className="mb-3">
									{constants.AdmissionAssessPara}
								</p>
								<Form.Item
									name="admission_criteria"
									rules={[
										{
											required: true,
											message: constants.OptionValidationMessage,
										},
									]}
								>
									<Checkbox.Group className="w-full">
										<div className="flex flex-col gap-3">
											{constants.AdmissionCriteriaOptions.map((option) => (
												<Checkbox key={option.value} value={option.value}>
													{option.label}
												</Checkbox>
											))}
										</div>
									</Checkbox.Group>
								</Form.Item>

								<Divider className='my-7' />

								<Space wrap>
									<Button className='bg-sky-800 text-white uppercase' type='primary' onClick={handleRedirect}>
										{constants.Cancel}
									</Button>
									{
										isAdmin &&
										(
											<>
												<Button className='bg-sky-800 text-white uppercase' type='primary' htmlType="submit" disabled={isSigned}>
													{constants.Save}
												</Button>
												<Popconfirm title="You will not be able to Edit after you Esign the Notes" onConfirm={() => handleEsignConfirm("esign")}>
													<Button className='bg-sky-800 text-white uppercase' disabled={isSigned}>{constants.Esign}</Button>
												</Popconfirm>
												{
													isSigned ?
														<span className='text-sm italic'>E-signed by {noteData?.admission_assessment_data?.signed_by_name} on {moment(noteData?.admission_assessment_data?.signed_on).format(constants.dateformat)}</span> :
														<span></span>
												}
											</>
										)
									}
								</Space>

							</Form>
						</Card>
					</Spin>
				</div>
			</div>
		</div>
	);
};

export default AdmissionAssessForm;